import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useFlag } from 'constants/flags'
import ShareDescription, { DescriptionBox } from './ShareDescription'
import TimeAgo from 'components/TimeAgo'
import Link from 'components/Link'
import MemberList from './MemberList'
import Breakpoint from 'components/Breakpoint'
import DownloadSection from './DownloadSection'
import { DocumentSubscriptionToggle } from './SubscriptionToggle'
import { IsNotSignedIn } from 'containers/IsSignedIn'
import { useVersioning, VersionSwitcher } from 'modules/versioning'
import { isReady, preRenderfarmStatus } from 'utils/renderStatus'
import { useShareUpdateMutation, VersionFragment } from 'gqlTypes'
import useUserSignedIn from 'modules/user/useUserSignedIn'

import {
  Caption,
  SidebarContainer,
  SidebarHeaderContainer,
  SidebarHeading,
  SidebarSection,
  SidebarSectionHeader,
} from './ShareMetaSidebar.styles'

const ShareMetaSidebar = () => {
  const isComponentsWebViewOn = useFlag('components-web-view')
  const { share, versionShortId, ...versioning } = useVersioning()
  const location = useLocation()
  const isUserSignedIn = useUserSignedIn()

  const [isEditing, setEditing] = useState(false)

  const enableEditing = () => setEditing(true)
  const disableEditing = () => setEditing(false)

  const [updateShareDescription] = useShareUpdateMutation({
    redirectErrors: false,
    onError: 'unsafe-throw-exception',
    onCompleted: data => {
      if (data?.shareUpdate?.successful) {
        disableEditing()
      }
    },
  })

  const {
    libraryEnabled,
    userCanInspect,
    userCanUpdateSettings,
    userCanAccessVersions,
    userHasAcceptedInvitationToView,
    updatedAt,
    subscriptionStatus,
    commentsEnabled,
  } = share

  const currentVersion = !versioning.loading ? versioning.currentVersion : null
  const latestVersion = !versioning.loading ? versioning.latestVersion : null
  const previewFiles = currentVersion?.previewFiles

  const versionCreator = currentVersion?.creator?.name
  const assetStatus = currentVersion?.document?.assetStatus

  const createdAt = currentVersion
    ? new Date(currentVersion?.createdAt)
    : undefined

  const document = (currentVersion as VersionFragment)?.document!

  const userIsCreator = currentVersion?.userIsCreator
  const userCanOpenInApp = document?.userCanOpenInApp ?? false

  const renderStatusFromVersion = currentVersion?.document?.renderStatus

  const renderStatus =
    renderStatusFromVersion ||
    preRenderfarmStatus((previewFiles || []).length !== 0, updatedAt as string)

  const showDownloadSection =
    (userCanOpenInApp ||
      document?.downloadAvailable ||
      userCanUpdateSettings ||
      libraryEnabled ||
      userCanInspect ||
      userHasAcceptedInvitationToView ||
      assetStatus === 'AVAILABLE') &&
    isReady(renderStatus)

  const version = {
    currentVersion,
    latestVersion,
  }

  const showMemberList = userHasAcceptedInvitationToView

  return (
    <SidebarContainer isComponentsWebViewOn={isComponentsWebViewOn}>
      <React.Fragment>
        <SidebarSection dark>
          <SidebarSectionHeader>
            <SidebarHeaderContainer>
              <SidebarHeading size="small" title={versionCreator}>
                Uploaded by {userIsCreator ? 'you' : versionCreator}
              </SidebarHeading>
              <Caption m={0}>{version && <TimeAgo date={createdAt} />}</Caption>
            </SidebarHeaderContainer>
          </SidebarSectionHeader>
          {userCanUpdateSettings ? (
            <ShareDescription
              description={share.description || ''}
              editing={isEditing}
              enableEditing={enableEditing}
              disableEditing={disableEditing}
              onSubmit={(values: { description?: string }) => {
                updateShareDescription({
                  variables: {
                    identifier: share.identifier,
                    share: { description: values.description || '' },
                  },
                })
              }}
            />
          ) : (
            share.description && (
              <DescriptionBox>{share.description}</DescriptionBox>
            )
          )}

          {isUserSignedIn && commentsEnabled && (
            <DocumentSubscriptionToggle
              id={share.identifier!}
              status={subscriptionStatus}
              versionShortId={versionShortId}
            />
          )}
        </SidebarSection>

        {showMemberList && (
          <SidebarSection dark innserSection data-testid="members-list">
            <MemberList share={share} version={version} />
          </SidebarSection>
        )}

        {userCanAccessVersions && <VersionSwitcher />}
      </React.Fragment>

      {showDownloadSection && (
        <Breakpoint on="sm">
          <SidebarSection
            flex="0 1 auto"
            mt="auto"
            justifyContent="flex-end"
            borderTop>
            <DownloadSection
              shareIdentifier={share.identifier}
              versionShortId={versionShortId}
              libraryEnabled={libraryEnabled!}
              userCanUpdateSettings={userCanUpdateSettings}
              userCanOpenInApp={userCanOpenInApp}
              document={(currentVersion as VersionFragment)?.document!}
              userCanInspect={userCanInspect!}
              userIsMember={share.workspace.userIsMember}
              isViewingLatestVersion={versioning.isViewingLatestVersion}
            />
          </SidebarSection>
        </Breakpoint>
      )}

      <IsNotSignedIn>
        <SidebarSection flex="none" dark borderTop>
          <Caption size="small">
            New here? Sketch Cloud lets you upload and share your Sketch
            documents with anyone.{' '}
            <Link
              to={{
                pathname: '/signup',
                state: { from: location },
              }}>
              Learn More
            </Link>
          </Caption>
        </SidebarSection>
      </IsNotSignedIn>
    </SidebarContainer>
  )
}

export { ShareMetaSidebar }
